import { Assets, Container, Graphics, Rectangle, Sprite, Texture } from 'pixi.js';
import type { Vec2 } from '@nanocars/shared';
import { CAR_SHEET_FRAME_COUNT, CAR_SHEET_PATHS, type LiverySlot } from '../assets/manifest';

const FRAME_PX = 144;
const CAR_LENGTH_PX = 128;
const FRAME_COLS = 8;
// The frame is square (144px) but only 128px of it is the car's own length;
// drawing the whole frame at this many world metres makes the car exactly
// 1m long (matches the sim's car radius/collision size).
const FRAME_SIZE_M = FRAME_PX / CAR_LENGTH_PX;

const DEG_PER_FRAME = 360 / CAR_SHEET_FRAME_COUNT;

// The only ramp on the v1 track (kitchen-table's toast ramp) grants 0.55s of
// airborne time; used as the reference for "how airborne" a car currently
// is, since CarSnap only carries seconds-remaining, not the original grant.
const AIR_TIME_REF_S = 0.55;
const AIR_SCALE_BOOST = 0.35;

const SHADOW_COLOR = 0x000000;
const SHADOW_ALPHA = 0.35;
const ELIMINATED_ALPHA = 0.3;

export interface CarTransform {
  pos: Vec2;
  heading: number;
  air: number;
  eliminated?: boolean;
}

interface CarVisual {
  sprite: Sprite;
  shadow: Graphics;
  frames: Texture[];
}

function headingToFrame(heading: number): number {
  let deg = (heading * 180) / Math.PI;
  deg = ((deg % 360) + 360) % 360;
  return Math.round(deg / DEG_PER_FRAME) % CAR_SHEET_FRAME_COUNT;
}

// Renders every car in the race from the generated 32-direction liveried
// sprite sheets, plus a soft ellipse shadow underneath that grows and
// offsets while the car is airborne. Owns two layers so RaceScene can
// insert the skid-mark texture between shadows and props exactly where the
// brief's render-order calls for it.
export class CarRenderer {
  readonly shadowLayer = new Container();
  readonly carLayer = new Container();
  private readonly cars = new Map<number, CarVisual>();

  async ensureCar(id: number, liverySlot: LiverySlot): Promise<void> {
    if (this.cars.has(id)) return;

    const sheet = await Assets.load<Texture>(CAR_SHEET_PATHS[liverySlot]);
    const frames: Texture[] = [];
    for (let i = 0; i < CAR_SHEET_FRAME_COUNT; i++) {
      const col = i % FRAME_COLS;
      const row = Math.floor(i / FRAME_COLS);
      frames.push(
        new Texture({ source: sheet.source, frame: new Rectangle(col * FRAME_PX, row * FRAME_PX, FRAME_PX, FRAME_PX) }),
      );
    }

    const sprite = new Sprite(frames[0]);
    sprite.anchor.set(0.5);
    sprite.width = FRAME_SIZE_M;
    sprite.height = FRAME_SIZE_M;

    const shadow = new Graphics();

    this.carLayer.addChild(sprite);
    this.shadowLayer.addChild(shadow);
    this.cars.set(id, { sprite, shadow, frames });
  }

  update(id: number, t: CarTransform): void {
    const visual = this.cars.get(id);
    if (!visual) return;

    const airFrac = Math.max(0, Math.min(1, t.air / AIR_TIME_REF_S));
    const scale = 1 + AIR_SCALE_BOOST * airFrac;

    visual.sprite.texture = visual.frames[headingToFrame(t.heading)]!;
    visual.sprite.width = FRAME_SIZE_M * scale;
    visual.sprite.height = FRAME_SIZE_M * scale;
    visual.sprite.position.set(t.pos.x, t.pos.y);
    visual.sprite.rotation = 0; // orientation comes entirely from the frame choice, not a rotation transform
    visual.sprite.alpha = t.eliminated ? ELIMINATED_ALPHA : 1;

    const shadowRx = (FRAME_SIZE_M / 2) * (0.8 + 0.35 * airFrac);
    const shadowRy = (FRAME_SIZE_M / 2) * (0.5 + 0.2 * airFrac);
    const shadowOffset = 0.06 + 0.45 * airFrac;

    visual.shadow.clear();
    visual.shadow.ellipse(0, 0, shadowRx, shadowRy).fill({ color: SHADOW_COLOR, alpha: SHADOW_ALPHA * (1 - 0.3 * airFrac) });
    visual.shadow.position.set(t.pos.x, t.pos.y + shadowOffset);
    visual.shadow.visible = !t.eliminated;
  }

  hasCar(id: number): boolean {
    return this.cars.has(id);
  }

  // Releases every per-car visual. The per-frame Textures are sub-rectangle
  // views this class created over one shared sheet TextureSource, so each
  // one is destroyed while the sheet itself (owned and cached by Assets) is
  // left alone for the next race to reuse.
  destroy(): void {
    for (const visual of this.cars.values()) {
      for (const frame of visual.frames) frame.destroy(false);
      visual.sprite.destroy();
      visual.shadow.destroy();
    }
    this.cars.clear();
    this.carLayer.destroy({ children: true });
    this.shadowLayer.destroy({ children: true });
  }
}
